// Mock rows for the demo table. Deliberately generated rather than committed as a
// fixture: 5,000 rows is enough that virtualization is visibly doing work and any
// scroll jank shows up during QA rather than in S15 with real candidates — and
// generating them costs nothing in the bundle.
//
// Nothing here is real. S14 onwards uses actual candidates through TanStack Query.

export interface DemoRow {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly venture: string;
  readonly stage: DemoStage;
  readonly score: number | null;
  readonly appliedAt: string;
  readonly owner: string;
  /** Deliberately blank on some rows, to prove empty cells render as an em dash. */
  readonly source: string | null;
}

export type DemoStage = 'screening' | 'assessment' | 'interview' | 'offer' | 'rejected';

const FIRST = [
  'Rafiq', 'Nadia', 'Imran', 'Farhana', 'Shahid', 'Mitu', 'Tanvir', 'Sumaiya',
  'Arif', 'Nusrat', 'Kamal', 'Rehana', 'Sabbir', 'Tahmina', 'Jahangir', 'Ruma',
];
const LAST = [
  'Hossain', 'Islam', 'Kabir', 'Rahman', 'Alam', 'Barua', 'Chowdhury', 'Das',
  'Ahmed', 'Khan', 'Siddique', 'Uddin', 'Begum', 'Haque', 'Mia', 'Sultana',
];
const ROLES = [
  'Senior accountant', 'Accounts executive', 'Warehouse supervisor', 'Site engineer',
  'HR officer', 'Procurement lead', 'Maintenance technician', 'Data analyst',
];
const VENTURES = ['Nest Operations', 'Ravndor', 'Atlas Maintenance'];
const STAGES: readonly DemoStage[] = ['screening', 'assessment', 'interview', 'offer', 'rejected'];
const OWNERS = ['Nusrat Jahan', 'Tanvir Hussain', 'Imran Kabir'];
const SOURCES = ['Career site', 'Referral', 'LinkedIn', 'Walk-in', null];

/**
 * Deterministic pseudo-random, so a QA intern and a developer see the same 5,000
 * rows and can talk about "row 3,412" meaningfully. `Math.random` would make every
 * reload a different dataset and every bug report unreproducible.
 */
function seeded(index: number, salt: number): number {
  const x = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function pick<TItem>(items: readonly TItem[], index: number, salt: number): TItem {
  return items[Math.floor(seeded(index, salt) * items.length)];
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function makeDemoRows(count: number): readonly DemoRow[] {
  // One fixed origin, so dates do not drift between renders.
  const origin = Date.UTC(2026, 7, 25);

  return Array.from({ length: count }, (_, index) => {
    const stage = pick(STAGES, index, 4);
    return {
      id: `row-${index + 1}`,
      name: `${pick(FIRST, index, 1)} ${pick(LAST, index, 2)}`,
      role: pick(ROLES, index, 3),
      venture: pick(VENTURES, index, 7),
      stage,
      // A rejected candidate often has no score yet — another em dash case.
      score: stage === 'rejected' && seeded(index, 9) > 0.5
        ? null
        : Math.round(seeded(index, 5) * 100),
      appliedAt: new Date(origin - Math.floor(seeded(index, 6) * 120) * DAY_MS).toISOString(),
      owner: pick(OWNERS, index, 8),
      source: pick(SOURCES, index, 10),
    };
  });
}

export const STAGE_LABELS: Readonly<Record<DemoStage, string>> = {
  screening: 'Screening',
  assessment: 'Assessment',
  interview: 'Interview',
  offer: 'Offer',
  rejected: 'Rejected',
};
